import { useChat, useChatActions, useChatStoreApi } from '@ai-sdk-tools/store';
import type { ChatOnFinishCallback } from 'ai';
import { lastAssistantMessageIsCompleteWithToolCalls } from 'ai';
import { useLayoutEffect, useRef } from 'react';

import { useAutoResume } from './use-auto-resume';
import { useChatTransport } from './use-chat-transport';
import type { UIMessageWithMetadata } from '../../../lib/types';

interface UseNovaChatOptions {
  chatUuid: string;
  initialMessages?: UIMessageWithMetadata[];
  hasActiveStream?: boolean;
  onFinish?: ChatOnFinishCallback<UIMessageWithMetadata>;
  onError?: (error: Error) => void;
}

/**
 * Wrapper around useChat from @ai-sdk-tools/store with Nova-specific configuration.
 * Configures transport, handles stream resumability, and manages chat state.
 *
 * IMPORTANT: This hook MUST be called before any other components use useChatActions()
 * or other store hooks, as it configures the transport for the store.
 *
 * Note: Data parts are handled separately via useDataParts() hook, not through onData callback.
 */
export function useNovaChat({
  chatUuid,
  initialMessages = [],
  hasActiveStream = false,
  onFinish,
  onError,
}: UseNovaChatOptions) {
  const transport = useChatTransport();

  // Configure the chat store with transport - this must be called first
  const chat = useChat<UIMessageWithMetadata>({
    id: chatUuid,
    resume: false,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    transport, // This configures the store's transport
    messages: initialMessages,
    experimental_throttle: 100,
    onFinish,
    onError,
  });

  // Get chat actions to access setNewChat and reset
  const chatActions = useChatActions<UIMessageWithMetadata>();
  const chatStoreApi = useChatStoreApi<UIMessageWithMetadata>();

  // Track previous chat UUID and initial messages to detect changes
  const prevChatUuidRef = useRef(chatUuid);
  const prevInitialMessagesRef = useRef(initialMessages);

  // Reset messages when chat UUID changes or initial messages change
  useLayoutEffect(() => {
    const chatUuidChanged = prevChatUuidRef.current !== chatUuid;
    const initialMessagesChanged = prevInitialMessagesRef.current !== initialMessages;

    if (chatUuidChanged || initialMessagesChanged) {
      // Reset the store state and set new messages when switching chats
      // This ensures messages are properly loaded when clicking on an existing thread
      // or when starting a new chat

      // For new chats (empty messages and UUID changed), use setNewChat to properly reset state
      // For existing chats, just set the messages
      const isNewChat = initialMessages.length === 0 && chatUuidChanged;

      if (isNewChat) {
        chatActions.setNewChat(chatUuid, []);
      } else if (chatUuidChanged) {
        chatActions.setNewChat(chatUuid, initialMessages);
      } else {
        const latestServerMessage = initialMessages.at(-1);
        const shouldResetSettledChatState = latestServerMessage?.role === 'assistant' && chat.status !== 'streaming';

        if (shouldResetSettledChatState) {
          chatStoreApi.getState().clearTransientDataParts();
          chatActions.setNewChat(chatUuid, initialMessages);
        } else {
          chat.setMessages(initialMessages);
        }
      }

      // Update refs
      prevChatUuidRef.current = chatUuid;
      prevInitialMessagesRef.current = initialMessages;
    }
  }, [chatUuid, initialMessages, chat, chatActions, chatStoreApi]);

  // Auto-resume stream if the last message is from the user (indicating interrupted stream)
  // Only check after messages have been set (not during initial load or new chat)
  useAutoResume({
    autoResume: true,
    initialMessages,
    hasActiveStream,
    resumeStream: chat.resumeStream,
  });

  return chat;
}
